import { DropdownFieldUI } from '@axo/form/feature/form-input/DropdownField/DropdownFieldUI';
import { NumberFieldUI } from '@axo/form/feature/form-input/NumberField/NumberFieldUI';
import { useTranslation } from '@axo/shared/i18n';
import { Button } from '@axo/ui-core/components/Button';
import { Icon } from '@axo/ui-core/components/Icon';
import { Stack } from '@axo/ui-core/components/layout/item';
import { Heading } from '@axo/ui-core/components/typography';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Form } from '../../../../../components/Form';
import { Navigation } from '../../../../../components/Navigation';
import { useAxoFormContext } from '../../../../../context/AxoForm/AxoForm';
import useInitiatedTracking from '../../../../../hooks/tracking/useInitiatedTracking';
import { formatSpacedNumber } from '../../../../../utils/format';
import type { DenmarkFormData, Step5Data } from '../../FormData.dk.types';
import {
  electricityFieldOptions,
  heatingFieldOptions,
  insuranceFieldOptions,
  livingCostMonthlyFieldOption,
  transportFieldOptions,
  tvPhoneInternetFieldOptions,
  waterFieldOptions,
} from './config';
import styles from './expenses.module.scss';
import { PreviewExpenses } from './previewExpenses/PreviewExpenses';

const generateDefaultValues = (hasKids: boolean): Step5Data => ({
  'Expense/TVPhoneInternet/Value': 200,
  'Expense/Transport/Value': 200,
  'Expense/Electricity/Value': hasKids ? 1000 : 600,
  'Expense/Water/Value': hasKids ? 600 : 200,
  'Expense/Heating/Value': hasKids ? 1000 : 200,
  'Expense/Insurance/Value': hasKids ? 600 : 200,
});

const touchedFieldsOverride = [
  'Expense/TVPhoneInternet/Value',
  'Expense/Transport/Value',
  'Expense/Electricity/Value',
  'Expense/Water/Value',
  'Expense/Heating/Value',
  'Expense/Insurance/Value',
];

export function Step5() {
  const { t } = useTranslation();
  const { onChange, onSubmit, axoFormData } =
    useAxoFormContext<DenmarkFormData>();
  const hasChildren = Number(axoFormData['Frontend/AmountOfChildren']) > 0;

  const [isEditing, setIsEditing] = useState(false);
  const form = useForm({
    defaultValues: {
      ...generateDefaultValues(hasChildren),
    },
    mode: 'onBlur',
  });

  const {
    formState: { touchedFields, isSubmitting },
  } = form;

  useInitiatedTracking({
    shouldTriggerOnInitiated: Object.keys(touchedFields).length > 0,
  });

  const handleEditMode = () => {
    setIsEditing((prev) => !prev);
  };

  return (
    <FormProvider {...form}>
      <Form
        onSubmit={onSubmit}
        onChange={onChange}
        touchedFieldsOverride={touchedFieldsOverride}
      >
        <Stack direction="h" className={styles.headline} gap="2xs">
          <Heading size="xs" className={styles.heading}>
            {t('Expenses.headline')}
          </Heading>
          <Button
            variant="ghost"
            size="xs"
            className={styles.button}
            onClick={handleEditMode}
            data-testid="button-toggle-edit-mode"
          >
            <Icon
              size="l"
              name={isEditing ? 'check' : 'pencil-simple-line'}
              className={isEditing ? styles.checkIcon : ''}
            />
          </Button>
        </Stack>
        {isEditing ? (
          <>
            <div className={styles.hidden}>
              <DropdownFieldUI<Step5Data>
                name="Expense/TVPhoneInternet/Value"
                label={t('TVPhoneInternet.label')}
                placeholder={t('placeholder')}
                data-testid="expenses-tv-phone-internet-dropdown"
                valid
                {...tvPhoneInternetFieldOptions}
              />

              <DropdownFieldUI<Step5Data>
                name="Expense/Transport/Value"
                label={t('Transport.label')}
                placeholder={t('placeholder')}
                data-testid="expenses-transport-dropdown"
                valid
                {...transportFieldOptions}
              />
            </div>
            <DropdownFieldUI<Step5Data>
              name="Expense/Electricity/Value"
              label={t('Electricity.label')}
              placeholder={t('placeholder')}
              data-testid="expenses-electricity-dropdown"
              valid
              {...electricityFieldOptions}
            />

            <DropdownFieldUI<Step5Data>
              name="Expense/Water/Value"
              label={t('Water.label')}
              placeholder={t('placeholder')}
              data-testid="expenses-water-dropdown"
              valid
              {...waterFieldOptions}
            />

            <DropdownFieldUI<Step5Data>
              name="Expense/Heating/Value"
              label={t('Heating.label')}
              placeholder={t('placeholder')}
              data-testid="expenses-heating-dropdown"
              valid
              {...heatingFieldOptions}
            />

            <DropdownFieldUI<Step5Data>
              name="Expense/Insurance/Value"
              label={t('Insurance.label')}
              placeholder={t('placeholder')}
              data-testid="expenses-insurance-dropdown"
              valid
              {...insuranceFieldOptions}
            />
          </>
        ) : (
          <PreviewExpenses />
        )}

        <NumberFieldUI<Step5Data>
          name="LoanApplication/LivingCostMonthly"
          label={t('LivingCostMonthly.label')}
          data-testid="living-cost-input"
          format={formatSpacedNumber}
          suffix={t('suffix.currency')}
          {...livingCostMonthlyFieldOption}
        />

        <Navigation.Continue loading={isSubmitting}>
          {t('next')}
        </Navigation.Continue>
      </Form>
    </FormProvider>
  );
}
